import { GameScene } from '../GameScene';
import { TILE_SIZE } from '@shared/constants';
import { EntityState } from '@shared/types/entity';
import { getAreasAtTile } from '@shared/utils/areaUtils';
import type { MapAreaData } from '@shared/types/map';
import { getNPCTemplate } from '@shared/data/npcs';
import { getItemTemplate } from '@shared/data/items';
import { ArmorSubtype } from '@shared/types/item';
import { setUiAudioManager } from '../../audio/uiSound';
import { type AudioConfig, defaultAudioConfig } from '../../audio/AudioManager';
import {
  SOUNDS,
  SoundCategory,
  SOUND_CATEGORY_ORDER,
  COMBAT_DEFAULTS,
  MUSIC_LOWHP_THRESHOLD,
  MUSIC_LOWHP_RATE,
  HEARTBEAT_HP_THRESHOLD,
  EQUIP_SOUNDS,
  EQUIP_DEFAULT_SOUND,
  PICKUP_SOUNDS,
  PICKUP_DEFAULT_SOUND,
  goldCoinSound,
  shieldBlockSound,
  armorHitSound,
  type NpcSounds,
} from '@shared/data/sounds';

/** ID del FX de silencio (espejo del server: GameRoom SILENCE_FX_ID). */
const SILENCE_FX_ID = 'fx_silence_mute';

/** Key del loop de ambiente de zona (uno solo a la vez; crossfade al cambiar de área). */
const ZONE_AMBIENT_LOOP_KEY = 'zone_ambient_loop';

/**
 * Glue del audio con el mundo: corre por frame para mantener al AudioManager en sync con el estado
 * del jugador local y de la zona. Responsabilidades:
 *
 *  - **Oyente/viewport**: setea listener (tile del player local) + medio-viewport (para el alcance
 *    3D = medio-viewport + 10 tiles).
 *  - **Zona**: detecta entrar/salir del área "de audio" (la de mayor prioridad con música o
 *    ambiente); dispara enter/leaveSound, crossfade de música y el timer de ruidos ambientes.
 *  - **Vida baja**: acelera la música a 1.35x bajo 30% HP (si está habilitado).
 *  - **Heartbeat**: loop bajo 15% HP.
 *  - **Muerte**: enmudece el entorno con el filtro "burbuja".
 *
 * También centraliza el `unlock()` del AudioContext (primer gesto) y aplica la config guardada.
 * Ver docs/SOUND_AND_MUSIC.md.
 */
export class AudioWorldController {
  private unlocked = false;
  private currentAudioAreaId: string | null = null;
  private ambientTimer = 0;
  private ambientNextMs = 0;
  private musicSpedUp = false;
  private heartbeatOn = false;
  private deathOn = false;
  private silencedOn = false;

  constructor(private scene: GameScene) {}

  /** Llamar una vez en GameScene.create(). Registra unlock + aplica config guardada. */
  public init(): void {
    setUiAudioManager(this.scene.audio);
    // Desbloquear el AudioContext en el primer gesto del usuario (requisito de los browsers).
    const unlock = (): void => this.tryUnlock();
    this.scene.input.once('pointerdown', unlock);
    this.scene.input.keyboard?.once('keydown', unlock);
    // Cerrar el AudioContext al terminar la escena (evita fugas al reconectar).
    this.scene.events.once('shutdown', () => this.shutdown());
  }

  private tryUnlock(): void {
    if (this.unlocked) return;
    this.unlocked = true;
    void this.scene.audio.unlock();
    this.scene.audio.applyConfig(this.loadConfig());
  }

  /** Construye el AudioConfig a partir de las opciones guardadas (o defaults). */
  public loadConfig(): AudioConfig {
    const config = defaultAudioConfig();
    const saved = this.scene.persistence.loadClientOptions()?.audio;
    if (saved) {
      if (typeof saved.masterVolume === 'number') config.masterVolume = saved.masterVolume;
      if (typeof saved.musicLowHpSpeedup === 'boolean') {
        config.musicLowHpSpeedup = saved.musicLowHpSpeedup;
      }
      if (saved.categories) {
        for (const cat of SOUND_CATEGORY_ORDER) {
          const s = saved.categories[cat];
          if (s) config.categories[cat] = { enabled: !!s.enabled, volume: clamp01(s.volume) };
        }
      }
    }
    return config;
  }

  /** Corre cada frame desde GameScene.update(). */
  public update(delta: number): void {
    if (!this.unlocked || !this.scene.audio.isReady) return;
    if (!this.scene.room || !this.scene.localSessionId) return;

    const render = this.scene.playerObjects.get(this.scene.localSessionId);
    const state = this.scene.room.state.players.get(this.scene.localSessionId);
    if (!render || !state) return;

    // --- Oyente + viewport en tiles ---
    const tx = Math.floor(render.currentWorldX / TILE_SIZE);
    const ty = Math.floor(render.currentWorldY / TILE_SIZE);
    this.scene.audio.setListener(tx, ty);
    const view = this.scene.cameras.main.worldView;
    this.scene.audio.setViewportHalfTiles(
      view.width / TILE_SIZE / 2,
      view.height / TILE_SIZE / 2
    );

    // --- Muerte: filtro burbuja ---
    const dead = state.state === EntityState.DEAD;
    if (dead !== this.deathOn) {
      this.deathOn = dead;
      this.scene.audio.setDeathMuffle(dead);
    }

    // --- Silencio del jugador local: fade-out/in del mundo (espeja el FX fx_silence_mute) ---
    const silenced = this.localHasSilenceFx(state);
    if (silenced !== this.silencedOn) {
      this.silencedOn = silenced;
      this.setSilenced(silenced);
    }

    // --- Zona: música + enter/leave + ambiente ---
    this.updateAreaAudio(tx, ty, delta);

    // --- Vida baja: aceleración de música + heartbeat ---
    const maxHp = state.maxHp ?? 0;
    const hp = state.minHp ?? 0;
    const frac = maxHp > 0 ? hp / maxHp : 1;
    this.updateLowHp(frac, dead);

    // --- Loops posicionales de ítems únicos en el piso: refrescar su gain/pan al moverse el oyente. ---
    for (const key of this.scene.audio.activeLoopKeys()) {
      if (key.startsWith('unique_')) this.scene.audio.updateLoopSpatial(key);
    }
  }

  /** Elige el área de audio de mayor prioridad (última con música o ambiente) en el tile. */
  private pickAudioArea(tx: number, ty: number): MapAreaData | null {
    const areas = this.scene.currentMap?.areas;
    if (!areas) return null;
    const here = getAreasAtTile(areas, tx, ty) as MapAreaData[];
    let chosen: MapAreaData | null = null;
    for (const a of here) {
      if (
        a.musicId ||
        a.ambientLoopId ||
        a.ambientSounds ||
        a.enterSound ||
        a.leaveSound ||
        a.reverbIntensity != null
      )
        chosen = a;
    }
    return chosen;
  }

  private updateAreaAudio(tx: number, ty: number, delta: number): void {
    const area = this.pickAudioArea(tx, ty);
    const areaId = area?.id ?? null;

    if (areaId !== this.currentAudioAreaId) {
      // Salir del área anterior.
      const prev = this.findAreaById(this.currentAudioAreaId);
      if (prev?.leaveSound) this.scene.audio.play(prev.leaveSound, { tileX: tx, tileY: ty });
      // Entrar a la nueva.
      if (area?.enterSound) this.scene.audio.play(area.enterSound, { tileX: tx, tileY: ty });
      // Música: crossfade a la de la zona (o cortar si no tiene).
      this.scene.audio.playMusic(area?.musicId ?? null);
      // Loop de ambiente de zona (lluvia/mar/viento…): crossfade al de la nueva zona (o cortar).
      this.scene.audio.stopLoop(ZONE_AMBIENT_LOOP_KEY, 800);
      if (area?.ambientLoopId) {
        this.scene.audio.startLoop(area.ambientLoopId, {}, ZONE_AMBIENT_LOOP_KEY);
      }
      // Reverb de "cueva": rampa al mix de la nueva zona (0 al salir a una zona sin reverb).
      this.scene.audio.setReverb(area?.reverbIntensity ?? 0);
      // Reiniciar timer de ruidos random.
      this.currentAudioAreaId = areaId;
      this.ambientTimer = 0;
      this.ambientNextMs = area?.ambientSounds ? this.randAmbientMs(area) : 0;
    }

    // Ruidos ambientes aleatorios de la zona.
    if (area?.ambientSounds && area.ambientSounds.ids.length > 0) {
      this.ambientTimer += delta;
      if (this.ambientTimer >= this.ambientNextMs) {
        this.ambientTimer = 0;
        this.ambientNextMs = this.randAmbientMs(area);
        const ids = area.ambientSounds.ids;
        const id = ids[Math.floor(Math.random() * ids.length)];
        // Posicional cerca del player (leve dispersión para que no siempre venga del centro).
        const ox = tx + Math.round((Math.random() - 0.5) * 8);
        const oy = ty + Math.round((Math.random() - 0.5) * 8);
        this.scene.audio.playAt(id, ox, oy);
      }
    }
  }

  /** True si el estado del player local tiene el FX de silencio (`fx_silence_mute`) en auraFxIds. */
  private localHasSilenceFx(state: unknown): boolean {
    const aura = (state as { auraFxIds?: { length: number; [k: number]: string } }).auraFxIds;
    if (!aura || typeof aura.length !== 'number') return false;
    for (let i = 0; i < aura.length; i++) {
      if (aura[i] === SILENCE_FX_ID) return true;
    }
    return false;
  }

  private randAmbientMs(area: MapAreaData): number {
    const a = area.ambientSounds!;
    const min = Math.max(200, a.minMs);
    const max = Math.max(min, a.maxMs);
    return min + Math.random() * (max - min);
  }

  private findAreaById(id: string | null): MapAreaData | null {
    if (!id) return null;
    const areas = this.scene.currentMap?.areas as MapAreaData[] | undefined;
    return areas?.find((a) => a.id === id) ?? null;
  }

  private updateLowHp(frac: number, dead: boolean): void {
    const config = this.scene.audio.getConfig();
    // Música acelerada.
    const shouldSpeed = !dead && config.musicLowHpSpeedup && frac < MUSIC_LOWHP_THRESHOLD;
    if (shouldSpeed !== this.musicSpedUp) {
      this.musicSpedUp = shouldSpeed;
      this.scene.audio.setMusicRate(shouldSpeed ? MUSIC_LOWHP_RATE : 1);
    }
    // Heartbeat.
    const shouldBeat = !dead && frac < HEARTBEAT_HP_THRESHOLD;
    if (shouldBeat !== this.heartbeatOn) {
      this.heartbeatOn = shouldBeat;
      if (shouldBeat) this.scene.audio.startLoop(COMBAT_DEFAULTS.heartbeat);
      else this.scene.audio.stopLoop(COMBAT_DEFAULTS.heartbeat);
    }
  }

  /**
   * Fade de audio por silencio del jugador local (espeja el FX de silencio). Lo llama el sync de
   * silencio del cliente al activar/desactivar `fx_silence_mute` sobre el local player.
   */
  public setSilenced(silenced: boolean): void {
    if (!this.scene.audio.isReady) return;
    this.scene.audio.fadeAll(silenced ? 0 : 1, 600);
  }

  /** Reproduce un sonido de categoría existente (usado por el visor y hooks varios). Helper cortita. */
  public playIfKnown(id: string | undefined, tileX?: number, tileY?: number): void {
    if (!id || !SOUNDS[id]) return;
    if (typeof tileX === 'number' && typeof tileY === 'number') {
      this.scene.audio.playAt(id, tileX, tileY);
    } else {
      this.scene.audio.play(id);
    }
  }

  /** Tile actual de una entidad (player o NPC) para sonido posicional. `null` si no está renderizada. */
  public entityTile(entityId: string): { x: number; y: number } | null {
    const p = this.scene.playerObjects.get(entityId);
    if (p) {
      return { x: Math.floor(p.currentWorldX / TILE_SIZE), y: Math.floor(p.currentWorldY / TILE_SIZE) };
    }
    const n = this.scene.npcObjects.get(entityId) as unknown as
      | { lastTileX?: number; lastTileY?: number }
      | undefined;
    if (n && typeof n.lastTileX === 'number' && typeof n.lastTileY === 'number') {
      return { x: n.lastTileX, y: n.lastTileY };
    }
    return null;
  }

  /** Reproduce un sonido posicionado en la entidad (o 2D si no se la encuentra). */
  public playAtEntity(id: string | undefined, entityId: string): void {
    if (!id || !SOUNDS[id]) return;
    const t = this.entityTile(entityId);
    if (t) this.scene.audio.playAt(id, t.x, t.y);
    else this.scene.audio.play(id);
  }

  /** Golpe melee del atacante (swing). Usa el hitSound del arma si existe, si no el default melee. */
  public onMeleeSwing(entityId: string, weaponHitSound?: string): void {
    this.playAtEntity(weaponHitSound ?? COMBAT_DEFAULTS.melee, entityId);
  }

  /** Golpe FALLADO (esquivado): whoosh en el objetivo. */
  public onMiss(targetId: string): void {
    this.playAtEntity(COMBAT_DEFAULTS.miss, targetId);
  }

  /** Golpe BLOQUEADO con escudo: sonido según el subtipo del escudo del que bloquea (metal vs default). */
  public onBlock(targetId: string, shieldSubtype?: string): void {
    this.playAtEntity(shieldBlockSound(shieldSubtype), targetId);
  }

  /** Impacto recibido por un objetivo, según el subtipo de armadura del receptor (o default). */
  public onImpact(targetId: string, armorSubtype?: string): void {
    this.playAtEntity(armorHitSound(armorSubtype), targetId);
  }

  /** Sonidos configurados del template de un NPC (por su entityId en el room state). */
  private npcSounds(entityId: string): NpcSounds | undefined {
    const st = this.scene.room?.state.npcs.get(entityId) as unknown as
      | { templateId?: string }
      | undefined;
    const tmpl = st?.templateId ? getNPCTemplate(st.templateId) : null;
    return tmpl?.sounds;
  }

  /**
   * Reproduce un sonido de acción de un NPC (greet/trade/revive/teachSpell/learnFail/...). No-op si
   * el NPC no define ese sonido (la mayoría no lo hace). Posicional en el NPC.
   */
  public playNpcSound(entityId: string, key: keyof NpcSounds): void {
    const id = this.npcSounds(entityId)?.[key];
    if (id) this.playAtEntity(id, entityId);
  }

  /** Sonido al ganar oro (delta>0), por tramo de cantidad. 2D (evento privado). */
  public playGoldGainSound(delta: number): void {
    if (delta > 0) this.scene.audio.play(goldCoinSound(delta));
  }

  /** Sonido al equipar un ítem, por su subtipo de armadura (pesada/ligera/tela) o default. */
  public playEquipSound(itemId: string): void {
    const sub = getItemTemplate(itemId)?.subtype;
    const id = sub && sub in EQUIP_SOUNDS ? EQUIP_SOUNDS[sub as ArmorSubtype] : EQUIP_DEFAULT_SOUND;
    this.scene.audio.play(id);
  }

  /** Sonido al levantar un ítem del piso, por su subtipo de armadura o default. */
  public playPickupSound(itemId: string): void {
    const sub = getItemTemplate(itemId)?.subtype;
    const id = sub && sub in PICKUP_SOUNDS ? PICKUP_SOUNDS[sub as ArmorSubtype] : PICKUP_DEFAULT_SOUND;
    this.scene.audio.play(id);
  }

  /** Handler del broadcast de sonido del server (visor admin). */
  public handlePlaySound(soundId: string, x?: number, y?: number): void {
    if (!SOUNDS[soundId]) return;
    if (typeof x === 'number' && typeof y === 'number') this.scene.audio.playAt(soundId, x, y);
    else this.scene.audio.play(soundId);
  }

  public shutdown(): void {
    this.scene.audio.shutdown();
    setUiAudioManager(null);
  }
}

function clamp01(v: number): number {
  return Math.max(0, Math.min(1, typeof v === 'number' ? v : 1));
}
